class AsAboutPage extends HTMLElement {
  constructor() {
    super();

    this.storeSelectors = [];
    this.storeDetails = [];
    this.storePanels = [];
    this.storeContainer = null;
    this.materialComponents = [];
    this.expandedStoreIdentifier = null;
    this.videoContainers = [];
    this.pressLogoSliders = [];
  }

  connectedCallback() {
    this.materialComponents = Array.from(this.querySelectorAll('[data-material-component]'));
    this.storeSelectors = Array.from(this.querySelectorAll('[data-store-selector]'));
    this.storeDetails = Array.from(this.querySelectorAll('[data-store-detail]'));
    this.storePanels = Array.from(this.querySelectorAll('[data-store-panel]'));
    this.storeContainer = this.querySelector('.as-about-stores');
    this.storeGalleries = this.querySelector('.as-about-stores__galleries');
    this.videoContainers = Array.from(this.querySelectorAll('[data-editorial-video]'));

    this.initializeMaterialComponents();
    this.initializeStoreGalleryHeight();
    this.initializeEditorialVideos();
    this.initializePressLogoSliders();

    if (this.storeSelectors.length > 0) {
      this.storeSelectors.forEach((storeSelector) => {
        storeSelector.addEventListener('mouseenter', () => {
          this.previewStore(storeSelector.dataset.storeSelector);
        });
        storeSelector.addEventListener('focus', () => {
          this.previewStore(storeSelector.dataset.storeSelector);
        });
        storeSelector.addEventListener('click', () => {
          this.expandStore(storeSelector.dataset.storeSelector, false);
        });
        storeSelector.addEventListener('keydown', (event) => this.handleStoreKeydown(event));

      });

      this.storeDetails.forEach((storeDetail) => {
        const backControl = storeDetail.querySelector('[data-store-back]');

        backControl?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.collapseStore(true);
        });
        backControl?.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') {
            return;
          }

          event.preventDefault();
          event.stopPropagation();
          this.collapseStore(true);
        });
      });

      this.activateStore('0', false);
    }
  }

  initializePressLogoSliders() {
    this.pressLogoAbortController = new AbortController();
    const { signal } = this.pressLogoAbortController;
    this.pressLogoMediaQuery = window.matchMedia('(max-width: 767px)');
    this.pressLogoSliders = Array.from(this.querySelectorAll('.as-about-press__logos'));
    this.pressLogoSliders.forEach((slider) => {
      const $slider = window.jQuery?.(slider);
      if (!$slider?.length) return;
      const $ = window.jQuery;
      const track = document.createElement('div');
      track.className = 'as-about-press__logo-track';
      let offset = 0;
      let maxOffset = 0;
      let gesture = null;
      let suppressClick = false;
      const isRtl = getComputedStyle(slider).direction === 'rtl';
      const direction = isRtl ? 1 : -1;
      const measure = () => {
        maxOffset = Math.max(0, track.scrollWidth - slider.clientWidth);
        offset = Math.max(0, Math.min(offset, maxOffset));
      };
      const render = () => {
        offset = Math.max(0, Math.min(offset, maxOffset));
        track.style.transform = `translate3d(${direction * offset}px, 0, 0)`;
      };
      const release = () => {
        gesture = null;
        slider.classList.remove('is-dragging');
      };
      const point = (event) => {
        const original = event.originalEvent || event;
        const touch = original.touches?.[0] || original.changedTouches?.[0];
        return touch
          ? { x: touch.clientX, y: touch.clientY }
          : { x: original.clientX, y: original.clientY };
      };
      const updateLayout = () => {
        release();
        if (this.pressLogoMediaQuery.matches && !track.parentNode) {
          track.append(...slider.children);
          slider.append(track);
        } else if (!this.pressLogoMediaQuery.matches && track.parentNode) {
          slider.replaceChildren(...track.children);
          offset = 0;
        }
        measure();
        render();
      };

      $slider.on('mousedown.asPressLogos touchstart.asPressLogos', (event) => {
        const original = event.originalEvent || event;
        if (!this.pressLogoMediaQuery.matches || (original.type === 'mousedown' && original.button !== 0)) return;
        const start = point(event);
        measure();
        suppressClick = false;
        gesture = { x: start.x, y: start.y, offset, dragging: false };
      });
      $(window).on('mousemove.asPressLogos touchmove.asPressLogos', (event) => {
        if (!gesture) return;
        const current = point(event);
        const dx = current.x - gesture.x;
        const dy = current.y - gesture.y;
        if (!gesture.dragging) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) < 6) return;
          if (Math.abs(dy) > Math.abs(dx)) {
            release();
            return;
          }
          gesture.dragging = true;
          suppressClick = true;
          slider.classList.add('is-dragging');
        }
        if (gesture.dragging) {
          event.preventDefault();
        }
        offset = gesture.offset + direction * dx;
        render();
      });
      $(window).on('mouseup.asPressLogos touchend.asPressLogos touchcancel.asPressLogos', release);
      $slider.on('click.asPressLogos', (event) => {
        if (!suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
        suppressClick = false;
      });
      $slider.on('dragstart.asPressLogos', (event) => {
        if (this.pressLogoMediaQuery.matches) event.preventDefault();
      });
      $slider.on('focusin.asPressLogos', (event) => {
        if (!this.pressLogoMediaQuery.matches) return;
        const link = event.target.closest('.as-about-press__logo-link');
        if (!link) return;
        measure();
        const viewport = slider.getBoundingClientRect();
        const bounds = link.getBoundingClientRect();
        if (bounds.left < viewport.left) offset += direction * (viewport.left - bounds.left);
        else if (bounds.right > viewport.right) offset -= direction * (bounds.right - viewport.right);
        render();
      });
      this.pressLogoMediaQuery.addEventListener('change', updateLayout, { signal });
      const layoutObserver = new ResizeObserver(() => {
        measure();
        if (!gesture) render();
      });
      layoutObserver.observe(slider);
      layoutObserver.observe(track);
      signal.addEventListener('abort', () => {
        release();
        $slider.off('.asPressLogos');
        $(window).off('.asPressLogos');
        layoutObserver.disconnect();
        if (track.parentNode) slider.replaceChildren(...track.children);
      }, { once: true });
      updateLayout();
    });
  }

  disconnectedCallback() {
    this.pressLogoAbortController?.abort();
  }

  initializeStoreGalleryHeight() {
    if (!this.storeGalleries) {
      return;
    }

    const images = Array.from(this.storeGalleries.querySelectorAll('.as-about-store-gallery__image'));
    const updateHeight = () => {
      const galleryWidth = this.storeGalleries.clientWidth;
      if (!galleryWidth) {
        return;
      }

      const loadedImages = images.filter((image) => image.naturalWidth && image.naturalHeight);
      if (!loadedImages.length) {
        return;
      }

      const maxHeight = Math.max(
        ...loadedImages.map((image) => galleryWidth * image.naturalHeight / image.naturalWidth),
      );
      this.storeGalleries.style.setProperty('--as-about-store-gallery-height', `${maxHeight}px`);
    };

    images.forEach((image) => image.addEventListener('load', updateHeight, { once: false }));
    updateHeight();

    if ('ResizeObserver' in window) {
      this.storeGalleryResizeObserver = new ResizeObserver(updateHeight);
      this.storeGalleryResizeObserver.observe(this.storeGalleries);
    }
  }

  initializeEditorialVideos() {
    this.videoContainers.forEach((videoContainer) => {
      const videoFrame = videoContainer.querySelector('[data-video-frame]');
      const playButton = videoContainer.querySelector('[data-video-play]');
      const exitButton = videoContainer.querySelector('[data-video-exit]');
      const videoUrl = videoContainer.dataset.videoUrl;
      const videoRatio = Number(videoContainer.dataset.videoRatio) || (16 / 9);

      if (!videoFrame || !videoUrl) {
        return;
      }

      const isMobile = () => window.matchMedia('(max-width: 767px)').matches;
      const separator = videoUrl.includes('?') ? '&' : '?';
      const frameUrl = `${videoUrl}${separator}autoplay=0&mute=0&playsinline=1&enablejsapi=1&rel=0&controls=1&fs=0&modestbranding=0&iv_load_policy=0&disablekb=0`;
      let frameReady = false;
      let shouldPlay = false;
      let currentTime = 0;
      let lastMobileTap = 0;

      const postCommand = (frame, func, args = []) => {
        frame.contentWindow?.postMessage(
          JSON.stringify({ event: 'command', func, args }),
          '*',
        );
      };

      const isFullscreen = () =>
        document.fullscreenElement === videoContainer ||
        document.webkitFullscreenElement === videoContainer;

      const updateVideoCoverSize = () => {
        const containerWidth = videoContainer.clientWidth;
        const containerHeight = videoContainer.clientHeight;

        if (!containerWidth || !containerHeight) {
          return;
        }

        const containerRatio = containerWidth / containerHeight;
        let frameWidth = containerWidth;
        let frameHeight = containerHeight;
        const fullscreen = isFullscreen();

        if (!fullscreen) {
          // Always cover the editorial media and crop overflow in the normal layout.
          if (containerRatio > videoRatio) {
            frameHeight = containerWidth / videoRatio;
          } else {
            frameWidth = containerHeight * videoRatio;
          }
          // Crop the iframe edges so YouTube's title and controls stay outside the normal viewport.
          const cropHeight = 146;
          const cropScale = (frameHeight + cropHeight) / frameHeight;
          frameHeight += cropHeight;
          frameWidth *= cropScale;
        }
        videoFrame.style.width = `${frameWidth}px`;
        videoFrame.style.height = `${frameHeight}px`;
      };

      const startVideo = () => {
        shouldPlay = true;

        if (!frameReady || videoContainer.dataset.videoPlaying === 'true') {
          return;
        }

        postCommand(videoFrame, 'unMute');
        postCommand(videoFrame, 'playVideo');
        videoContainer.dataset.videoPlaying = 'true';
        videoContainer.classList.add('is-playing');
      };
      const resetVideo = () => {
        shouldPlay = false;
        videoContainer.dataset.videoPlaying = 'false';
        videoContainer.classList.remove('is-playing');
      };
      const pauseVideo = () => {
        if (isMobile() || isFullscreen()) {
          return;
        }

        shouldPlay = false;
        postCommand(videoFrame, 'pauseVideo');
        videoContainer.dataset.videoPlaying = 'false';
      };

      const prepareFrame = (frame, onReady) => {
        frame.addEventListener('load', () => {
          onReady();
          videoContainer.classList.add('is-ready');
          frame.contentWindow?.postMessage(
            JSON.stringify({ event: 'listening', id: frame.id, channel: 'as-about-page' }),
            '*',
          );
          postCommand(frame, 'addEventListener', ['onStateChange']);
        }, { once: true });
      };

      prepareFrame(videoFrame, () => {
        frameReady = true;
        if (shouldPlay && !isFullscreen()) {
          startVideo();
        }
      });

      window.addEventListener('message', (event) => {
        if (
          event.source !== videoFrame.contentWindow ||
          !event.data
        ) {
          return;
        }

        let message;
        try {
          message = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        } catch {
          return;
        }

        if (message.event === 'infoDelivery' && message.info?.currentTime != null) {
          currentTime = Number(message.info.currentTime) || currentTime;
        }

        if (message.event === 'onStateChange' && Number(message.info) === 0) {
          resetVideo();
          if (isFullscreen()) {
            document.exitFullscreen?.();
          }
        }
      });
      videoFrame.src = frameUrl;
      updateVideoCoverSize();

      if ('ResizeObserver' in window) {
        const videoResizeObserver = new ResizeObserver(updateVideoCoverSize);
        videoResizeObserver.observe(videoContainer);
      }
      document.addEventListener('fullscreenchange', updateVideoCoverSize);

      videoContainer.addEventListener('mouseenter', () => {
        if (!isMobile() && !isFullscreen()) {
          startVideo();
        }
      });
      videoContainer.addEventListener('mouseleave', pauseVideo);
      playButton?.addEventListener('click', (event) => {
        event.stopPropagation();
        startVideo();
      });
      exitButton?.addEventListener('click', () => {
        if (isFullscreen()) {
          document.exitFullscreen?.();
        }
      });

      const enterFullscreen = () => {
        if (!videoContainer.classList.contains('is-playing') || isFullscreen()) {
          return;
        }

        const requestFullscreen =
          videoContainer.requestFullscreen || videoContainer.webkitRequestFullscreen;
        const fullscreenPromise = requestFullscreen?.call(videoContainer);
        postCommand(videoFrame, 'getCurrentTime');
        videoContainer.classList.add('is-fullscreen-mode');
        fullscreenPromise?.catch?.(() => {
          videoContainer.classList.remove('is-fullscreen-mode');
        });

        window.setTimeout(() => {
          if (!frameReady) {
            return;
          }

          postCommand(videoFrame, 'seekTo', [Math.floor(currentTime), true]);
          postCommand(videoFrame, 'unMute');
          postCommand(videoFrame, 'playVideo');
        }, 150);
      };

      videoContainer.addEventListener('click', (event) => {
        if (event.target.closest('[data-video-exit]')) {
          return;
        }

        if (isMobile()) {
          startVideo();
          return;
        }

        enterFullscreen();
      });
      videoContainer.addEventListener('touchend', (event) => {
        if (!isMobile() || event.target.closest('[data-video-exit]')) {
          return;
        }

        const now = Date.now();
        if (now - lastMobileTap < 350) {
          event.preventDefault();
          lastMobileTap = 0;
          enterFullscreen();
          return;
        }

        lastMobileTap = now;
        startVideo();
      });
      document.addEventListener('fullscreenchange', () => {
        updateVideoCoverSize();
        if (!isFullscreen()) {
          videoContainer.classList.remove('is-fullscreen-mode');
          if (frameReady && videoContainer.dataset.videoPlaying === 'true') {
            postCommand(videoFrame, 'seekTo', [Math.floor(currentTime), true]);
            postCommand(videoFrame, 'playVideo');
          }
        }
      });
      document.addEventListener('webkitfullscreenchange', () => {
        updateVideoCoverSize();
        if (!isFullscreen()) {
          videoContainer.classList.remove('is-fullscreen-mode');
        }
      });
    });
  }

  initializeMaterialComponents() {
    this.materialComponents.forEach((materialComponent) => {
      materialComponent.materialPanels = Array.from(materialComponent.querySelectorAll('[data-material-panel]'));
      materialComponent.materialTriggers = Array.from(materialComponent.querySelectorAll('[data-material-trigger]'));
      materialComponent.backButtons = Array.from(materialComponent.querySelectorAll('[data-material-back]'));
      materialComponent.lastTrigger = null;

      materialComponent.materialTriggers.forEach((materialTrigger) => {
        materialTrigger.addEventListener('click', () => {
          materialComponent.lastTrigger = materialTrigger;
          this.activateMaterialPanel(materialComponent, materialTrigger.dataset.materialTrigger, true);
        });
      });

      materialComponent.backButtons.forEach((backButton) => {
        backButton.addEventListener('click', () => {
          this.activateMaterialPanel(materialComponent, 'overview', false);

          if (materialComponent.lastTrigger) {
            materialComponent.lastTrigger.focus({ preventScroll: true });
          }
        });
      });

      this.activateMaterialPanel(materialComponent, 'overview', false);
    });
  }

  activateMaterialPanel(materialComponent, panelIdentifier, moveFocus) {
    const selectedPanel = materialComponent.materialPanels.find(
      (materialPanel) => materialPanel.dataset.materialPanel === panelIdentifier,
    );

    if (!selectedPanel) {
      return;
    }

    materialComponent.dataset.materialState = panelIdentifier;

    materialComponent.materialPanels.forEach((materialPanel) => {
      const isSelected = materialPanel === selectedPanel;
      materialPanel.hidden = !isSelected;
      materialPanel.setAttribute('aria-hidden', String(!isSelected));
    });

    materialComponent.materialTriggers.forEach((materialTrigger) => {
      const controlsSelectedPanel = materialTrigger.getAttribute('aria-controls') === selectedPanel.id;
      materialTrigger.setAttribute('aria-expanded', String(controlsSelectedPanel));
    });

    if (moveFocus && panelIdentifier !== 'overview') {
      const focusTarget = selectedPanel.querySelector('[data-material-panel-heading], [data-material-back]');
      focusTarget?.focus({ preventScroll: true });
    }
  }

  activateStore(storeIdentifier, focusSelector) {
    const normalizedStoreIdentifier = String(storeIdentifier);
    const selectedStore = this.storeSelectors.find(
      (storeSelector) => storeSelector.dataset.storeSelector === normalizedStoreIdentifier,
    );

    if (!selectedStore) {
      return;
    }

    this.storeSelectors.forEach((storeSelector) => {
      const isSelected = storeSelector.dataset.storeSelector === normalizedStoreIdentifier;
      storeSelector.setAttribute('aria-selected', String(isSelected));
      storeSelector.tabIndex = isSelected ? 0 : -1;
    });

    this.storeDetails.forEach((storeDetail) => {
      storeDetail.hidden =
        this.expandedStoreIdentifier === null || storeDetail.dataset.storeDetail !== normalizedStoreIdentifier;
    });

    this.storePanels.forEach((storePanel) => {
      storePanel.hidden = storePanel.dataset.storePanel !== normalizedStoreIdentifier;
    });

    if (focusSelector) {
      selectedStore.focus({ preventScroll: true });
    }

  }

  previewStore(storeIdentifier) {
    if (this.expandedStoreIdentifier !== null || window.matchMedia('(max-width: 767px)').matches) {
      return;
    }

    this.activateStore(storeIdentifier, false);
  }

  expandStore(storeIdentifier, focusSelector) {
    if (window.matchMedia('(max-width: 767px)').matches) {
      return;
    }

    const normalizedStoreIdentifier = String(storeIdentifier);
    const selectedStore = this.storeSelectors.find(
      (storeSelector) => storeSelector.dataset.storeSelector === normalizedStoreIdentifier,
    );

    if (!selectedStore) {
      return;
    }

    this.expandedStoreIdentifier = normalizedStoreIdentifier;
    this.storeContainer?.setAttribute('data-store-expanded', 'true');
    this.storeSelectors.forEach((storeSelector) => {
      storeSelector.hidden = true;
      storeSelector.setAttribute('aria-selected', String(storeSelector === selectedStore));
      storeSelector.tabIndex = -1;
      storeSelector.dataset.storeExpanded = 'true';
    });
    this.storeDetails.forEach((storeDetail) => {
      storeDetail.hidden = storeDetail.dataset.storeDetail !== normalizedStoreIdentifier;
    });
    this.activateStore(normalizedStoreIdentifier, focusSelector);
  }

  collapseStore(moveFocus) {
    if (this.expandedStoreIdentifier === null) {
      return;
    }

    const previouslyExpandedSelector = this.storeSelectors.find(
      (storeSelector) => storeSelector.dataset.storeSelector === this.expandedStoreIdentifier,
    );

    this.expandedStoreIdentifier = null;
    this.storeContainer?.removeAttribute('data-store-expanded');

    this.storeDetails.forEach((storeDetail) => {
      storeDetail.hidden = true;
    });

    this.storeSelectors.forEach((storeSelector) => {
      storeSelector.hidden = false;
      storeSelector.removeAttribute('data-store-expanded');
      storeSelector.tabIndex = storeSelector.dataset.storeSelector === '0' ? 0 : -1;
    });

    this.activateStore('0', false);

    if (moveFocus) {
      previouslyExpandedSelector?.focus({ preventScroll: true });
    }
  }

  handleStoreKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.expandStore(event.currentTarget.dataset.storeSelector, false);
      return;
    }

    const currentStoreIndex = this.storeSelectors.indexOf(event.currentTarget);
    let nextStoreIndex = currentStoreIndex;

    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      nextStoreIndex = (currentStoreIndex + 1) % this.storeSelectors.length;
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      nextStoreIndex = (currentStoreIndex - 1 + this.storeSelectors.length) % this.storeSelectors.length;
    } else if (event.key === 'Home') {
      nextStoreIndex = 0;
    } else if (event.key === 'End') {
      nextStoreIndex = this.storeSelectors.length - 1;
    } else {
      return;
    }

    event.preventDefault();
    this.activateStore(this.storeSelectors[nextStoreIndex].dataset.storeSelector, true);
  }
}

if (!customElements.get('as-about-page')) {
  customElements.define('as-about-page', AsAboutPage);
}
